/* Location message — personalized shipping message with visitor geolocation (scoped, no globals) */
(() => {
  if (window.truLocationMessage) return;

  const SELECTOR_TEXT = '[data-location-type]';
  const SELECTOR_PLACE = '[data-location-place]';
  const SELECTOR_DATE = '[data-location-date]';
  const SELECTOR_ANIM = '[data-location-anim]';

  const CACHE_KEY = 'tru_location_cache';
  const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h — one geo lookup per visitor per day, shared across tabs
  const ANIMATION_MS = 2400;
  // Optional keyed geo proxy (Cloudflare Worker returning {city, region, region_code}), tried before the keyless providers
  const GEO_PROXY_URL = '';
  const PROVIDER_TIMEOUT_MS = 2500;
  const COOLDOWN_KEY = 'tru_location_cooldown';
  const COOLDOWN_MS = 60 * 60 * 1000; // skip a provider for 1h after it fails (quota/429/outage)

  let pendingFetch = null;
  let lookupFailed = false;
  let memLocation = null;

  function getCachedLocation() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || Date.now() - data.ts > CACHE_TTL) return null;
      return hasUsableLocation(data.location) ? data.location : null;
    } catch (e) {
      return null;
    }
  }

  function hasUsableLocation(location) {
    return !!(location && (location.city || location.region || location.region_code));
  }

  function setCachedLocation(location) {
    if (!hasUsableLocation(location)) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), location }));
    } catch (e) {}
  }

  function formatDeliveryDate(days) {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(days, 10));
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  function formatLocation(data, type) {
    if (!data) return null;
    switch (type) {
      case 'country':
        return data.country;
      case 'region':
        return data.region || data.region_code;
      case 'city_region': {
        const city = data.city;
        const region = data.region || data.region_code;
        if (city && region) return `${city}, ${region}`;
        return city || region || data.country;
      }
      case 'city':
      default:
        return data.city || data.region || data.country;
    }
  }

  // Keyless HTTPS+CORS providers, tried in order; each maps its payload to {city, region, region_code}
  const PROVIDERS = [
    { id: 'ipwho', url: 'https://ipwho.is/', ok: (d) => d.success !== false, map: (d) => ({ city: d.city, region: d.region, region_code: d.region_code }) },
    { id: 'geojs', url: 'https://get.geojs.io/v1/ip/geo.json', ok: () => true, map: (d) => ({ city: d.city, region: d.region }) },
    { id: 'freeipapi', url: 'https://free.freeipapi.com/api/v1/json', ok: () => true, map: (d) => ({ city: d.cityName, region: d.regionName, region_code: d.regionCode }) },
    { id: 'ipinfo', url: 'https://ipinfo.io/json', ok: () => true, map: (d) => ({ city: d.city, region: d.region }) },
    { id: 'ipapi', url: 'https://ipapi.co/json/', ok: (d) => !d.error, map: (d) => ({ city: d.city, region: d.region, region_code: d.region_code }) },
  ];
  if (GEO_PROXY_URL) {
    PROVIDERS.unshift({ id: 'proxy', url: GEO_PROXY_URL, ok: (d) => !d.error, map: (d) => d });
  }

  function getCooldowns() {
    try {
      return JSON.parse(localStorage.getItem(COOLDOWN_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function coolDown(id) {
    try {
      const cooldowns = getCooldowns();
      cooldowns[id] = Date.now() + COOLDOWN_MS;
      localStorage.setItem(COOLDOWN_KEY, JSON.stringify(cooldowns));
    } catch (e) {}
  }

  function queryProvider(provider) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), PROVIDER_TIMEOUT_MS);

    return fetch(provider.url, { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(provider.id + ' http ' + res.status);
        return res.json();
      })
      .then((data) => {
        if (!data || !provider.ok(data)) throw new Error(provider.id + ' rejected');
        const location = provider.map(data);
        if (!hasUsableLocation(location)) throw new Error(provider.id + ' empty');
        return location;
      })
      .finally(() => window.clearTimeout(timer));
  }

  function fetchCountryFallback() {
    const rootPath = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
    const endpoint = (rootPath + 'browsing_context_suggestions.json').replace(/\/+/g, '/');

    return fetch(endpoint)
      .then((res) => res.json())
      .then((data) => {
        const countryName = data && data.detected_values && data.detected_values.country && data.detected_values.country.name;
        if (!countryName) throw new Error('Shopify fallback failed');
        return { country: countryName };
      });
  }

  function fetchVisitorLocation() {
    const cooldowns = getCooldowns();
    const available = PROVIDERS.filter((provider) => !(cooldowns[provider.id] > Date.now()));

    return available
      .reduce(
        (chain, provider) =>
          chain.catch(() =>
            queryProvider(provider).catch((err) => {
              coolDown(provider.id);
              throw err;
            })
          ),
        Promise.reject(new Error('no provider'))
      )
      .catch(fetchCountryFallback);
  }

  function applyDeliveryDates() {
    document.querySelectorAll(SELECTOR_DATE).forEach((el) => {
      const days = el.getAttribute('data-location-date');
      if (days) el.textContent = formatDeliveryDate(days);
    });
  }

  function applyLocations(locationData) {
    document.querySelectorAll(SELECTOR_TEXT).forEach((el) => {
      const type = el.getAttribute('data-location-type') || 'city';
      const fallback = el.getAttribute('data-location-fallback');
      const placeEl = el.querySelector(SELECTOR_PLACE);
      if (!placeEl) return;

      const formatted = formatLocation(locationData, type) || fallback;
      if (formatted) placeEl.textContent = formatted;
    });
  }

  function playAnimations() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll(SELECTOR_ANIM).forEach((el) => {
      if (el.hasAttribute('data-location-played')) return;
      el.setAttribute('data-location-played', '');
      el.classList.remove('is-animating', 'is-complete');

      if (reducedMotion) {
        el.classList.add('is-complete');
        return;
      }

      void el.offsetWidth;
      el.classList.add('is-animating');

      window.setTimeout(() => {
        el.classList.remove('is-animating');
        el.classList.add('is-complete');
      }, ANIMATION_MS);
    });
  }

  function init() {
    if (!document.querySelector(SELECTOR_TEXT)) return;

    applyDeliveryDates();
    playAnimations();

    const cached = memLocation || getCachedLocation();
    if (cached) {
      applyLocations(cached);
      return;
    }

    if (lookupFailed) return;

    if (!pendingFetch) {
      pendingFetch = fetchVisitorLocation()
        .then((locationData) => {
          memLocation = locationData;
          setCachedLocation(locationData);
          return locationData;
        })
        .catch((err) => {
          console.warn('Location message: all geolocation routes failed', err);
          lookupFailed = true;
          return null;
        })
        .finally(() => {
          pendingFetch = null;
        });
    }

    pendingFetch.then((locationData) => {
      if (locationData) applyLocations(locationData);
    });
  }

  window.truLocationMessage = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  // Instances injected after load (variant/section re-renders, cloned buy boxes)
  let scheduled = false;
  new MutationObserver((mutations) => {
    if (scheduled) return;
    const added = mutations.some((m) =>
      Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches(SELECTOR_TEXT) || n.querySelector(SELECTOR_TEXT)))
    );
    if (!added) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      init();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
